import Tooltip from "src/components/Tooltip";
import {
  Container,
  TaskRow,
  StatusIcon,
  TeamMemberLabel,
  TaskDetailLabel,
  DotDivider,
  DateLabel,
  NoTasksLabel,
  ProgressLine,
} from "./SectionContent.styles";
import {
  TaskInProgressBadge,
  TaskToDoBadge,
  TaskCorrectionNeededBadge,
  TaskCompletedBadge,
} from "./StatusBadges";
import {
  ProgressCircleInProgressIcon,
  ProgressCircleCompletedIcon,
  ProgressCircleTodoIcon,
} from "src/assets/icons";
import { timeAgo } from "src/utils";
import Monogram from "src/components/Monogram";

type TaskStatus =
  "not_done" | "in_progress" | "done" | "cancelled" | "correction_needed";

export interface Task {
  status: TaskStatus;
  name: string;
  updated_at: string;
  team_members: { first_name: string; last_name: string }[];
}

const shouldDisableToolTip = (task: string, status: string): boolean => {
  const maxCharCount = 98;
  return task.length + status.length < maxCharCount;
};

export default ({ tasks }: { tasks: Task[] }) => {
  if (!tasks || tasks.length === 0) {
    return <NoTasksLabel>No tasks have been added at this time</NoTasksLabel>;
  }
  const getIconForTask = (task: Task): JSX.Element => {
    if (task.status === "in_progress") {
      return <ProgressCircleInProgressIcon />;
    } else if (task.status === "done") {
      return <ProgressCircleCompletedIcon />;
    } else {
      return (
        <ProgressCircleTodoIcon
          opacity="1"
          fill="#f3f5f8"
          strokeColor="#E4E7EC"
        />
      );
    }
  };
  const firstTeamMemberName = (task: Task): string => {
    if (task.team_members.length === 0) return "";
    const person: { first_name: string; last_name: string } =
      task.team_members[0];
    return `${person.first_name} ${person.last_name.charAt(0)}.`;
  };

  return (
    <Container>
      <ProgressLine />
      {tasks.map((task) => (
        <TaskRow key={task.name}>
          <StatusIcon>{getIconForTask(task)}</StatusIcon>
          {firstTeamMemberName(task).length > 0 && (
            <>
              <Monogram size={24}>
                {firstTeamMemberName(task).charAt(0) ?? ""}
              </Monogram>
              <TeamMemberLabel>{firstTeamMemberName(task)}</TeamMemberLabel>
            </>
          )}
          {task.status === "in_progress" && (
            <TaskInProgressBadge>In Review</TaskInProgressBadge>
          )}
          {task.status === "not_done" && <TaskToDoBadge>To Do</TaskToDoBadge>}
          {task.status === "correction_needed" && (
            <TaskCorrectionNeededBadge>
              Correction Needed
            </TaskCorrectionNeededBadge>
          )}
          {task.status === "done" && (
            <TaskCompletedBadge>Done</TaskCompletedBadge>
          )}
          <Tooltip
            tooltipContent={task.name}
            disableTooltip={shouldDisableToolTip(task.name, task.status)}
          >
            <TaskDetailLabel>{task.name}</TaskDetailLabel>
          </Tooltip>
          <DotDivider />
          <DateLabel>{timeAgo(task.updated_at)}</DateLabel>
        </TaskRow>
      ))}
    </Container>
  );
};
